import { useCallback, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import { Popover } from '@/mastodon/components/popover';
import HourglassIcon from '@/material-icons/400-24px/hourglass.svg?react';
import { changeComposeExpiresIn } from 'mastodon/actions/compose_typed';
import { Icon } from 'mastodon/components/icon';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

const messages = defineMessages({
  label: { id: 'compose.expiration.change', defaultMessage: 'Set expiration' },
  header: {
    id: 'compose.expiration.header',
    defaultMessage: 'This post deletes after:',
  },
  none: { id: 'compose.expiration.none', defaultMessage: 'None' },
  minutes: {
    id: 'intervals.full.minutes',
    defaultMessage: '{number, plural, one {# minute} other {# minutes}}',
  },
  hours: {
    id: 'intervals.full.hours',
    defaultMessage: '{number, plural, one {# hour} other {# hours}}',
  },
  days: {
    id: 'intervals.full.days',
    defaultMessage: '{number, plural, one {# day} other {# days}}',
  },
});

export const ExpirationDropdown: React.FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(
    null,
  );
  const expiresIn = useAppSelector(
    (state) => state.compose.get('expires_in') as number | null,
  );

  const options: { value: number | null; label: string }[] = [
    { value: null, label: intl.formatMessage(messages.none) },
    { value: 300, label: intl.formatMessage(messages.minutes, { number: 5 }) },
    {
      value: 3600,
      label: intl.formatMessage(messages.minutes, { number: 60 }),
    },
    { value: 21600, label: intl.formatMessage(messages.hours, { number: 6 }) },
    { value: 86400, label: intl.formatMessage(messages.hours, { number: 24 }) },
    { value: 259200, label: intl.formatMessage(messages.days, { number: 3 }) },
    { value: 604800, label: intl.formatMessage(messages.days, { number: 7 }) },
  ];

  const currentOption = options.find((option) => option.value === expiresIn);

  const handleToggle = useCallback(() => {
    setOpen((value) => !value);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget.dataset;
      dispatch(changeComposeExpiresIn(value ? Number(value) : null));
      setOpen(false);
    },
    [dispatch],
  );

  return (
    <div className='expiration-dropdown'>
      <button
        type='button'
        ref={setTargetElement}
        title={intl.formatMessage(messages.label)}
        aria-expanded={open}
        onClick={handleToggle}
        className={classNames('dropdown-button', {
          active: open || expiresIn !== null,
        })}
      >
        <Icon id='hourglass' icon={HourglassIcon} />
        <span className='dropdown-button__label'>
          {currentOption?.label ?? intl.formatMessage(messages.none)}
        </span>
      </button>

      <Popover
        isOpen={open}
        onClose={handleClose}
        offset={5}
        placement='bottom-end'
        reference={targetElement}
      >
        {({ props, placement }) => (
          <div {...props}>
            <div
              className={`dropdown-animation dropdown-menu expiration-dropdown__menu ${placement}`}
            >
              <div className='expiration-dropdown__menu__header'>
                {intl.formatMessage(messages.header)}
              </div>

              {options.map((option) => (
                <label
                  key={option.value ?? 'none'}
                  className='expiration-dropdown__menu__option'
                >
                  <input
                    type='radio'
                    name='expires_in'
                    data-value={option.value ?? ''}
                    checked={option.value === expiresIn}
                    onChange={handleSelect}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
};
